import { useContext, useEffect, useMemo, useRef, useState } from "react";
import GooglePayButton from "@google-pay/button-react";
import { GlobalContext } from "../../../../Context/GlobalContext";
import { useTillGooglePayCheckout } from "../../../../Context/hook/useTillGooglePayCheckout";
import { usePayNow } from "../../../../Context/hook/usePayNow";
import { useNoteAlert } from "../../../../Context/pleaseNote";
import { Spinner } from "../../../../Layout/Common/spinner";
import { CardIcon } from "../../../../Layout/Common/CardIcon";
import { TILL_GOOGLEPAY_GATEWAY } from "../../../../constants";

const GOOGLE_PAY_SCRIPT = "https://pay.google.com/gp/p/js/pay.js";

let googlePayScriptPromise = null;
const loadGooglePayScript = () => {
  if (window.google?.payments?.api) return Promise.resolve(window.google);
  if (googlePayScriptPromise) return googlePayScriptPromise;
  googlePayScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOOGLE_PAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = GOOGLE_PAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return googlePayScriptPromise;
};

const baseCardPaymentMethod = {
  type: "CARD",
  parameters: {
    allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
    allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX"],
  },
};

// Public (non-secret) Till Google Pay config — baked into the bundle from REACT_APP_TILL_* env vars.
const TILL_GATEWAY_MERCHANT_ID = process.env.REACT_APP_TILL_GOOGLEPAY_MERCHANT_ID || "";
const TILL_ENVIRONMENT = process.env.REACT_APP_TILL_GOOGLE_ENVIRONMENT || "TEST";
const TILL_MERCHANT_NAME = process.env.REACT_APP_TILL_MERCHANT_NAME || "WES ALLIANCE TEST";
const TILL_CURRENCY = process.env.REACT_APP_TILL_CURRENCY || "AUD";
const TILL_COUNTRY = process.env.REACT_APP_TILL_COUNTRY || "AU";
// Google-issued merchant ID (BCR2DN…) from the Google Pay & Wallet Console. Required by Google Pay in
// PRODUCTION; ignored in TEST. Same ID regardless of gateway, so we reuse REACT_APP_GOOGLEPAY_MERCHANT_ID.
const GOOGLE_MERCHANT_ID = process.env.REACT_APP_GOOGLEPAY_MERCHANT_ID || "";

// Till Google Pay tokenization spec — gateway TILL_GOOGLEPAY_GATEWAY, gatewayMerchantId from env.
const tokenizationSpecification = TILL_GATEWAY_MERCHANT_ID
  ? {
      type: "PAYMENT_GATEWAY",
      parameters: { gateway: TILL_GOOGLEPAY_GATEWAY, gatewayMerchantId: TILL_GATEWAY_MERCHANT_ID },
    }
  : null;

export const TillGooglePayPayment = ({ handleOnCheck }) => {
  const { netAmount, selectedPaymentType, OrderId } = useContext(GlobalContext);
  const { processOrder } = useTillGooglePayCheckout(netAmount, OrderId);
  const { handleWalletPayClick, paybuttonDisabled } = usePayNow();
  const { showNoteAlert, showFlashScreen } = useNoteAlert();

  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(true);

  const clientRef = useRef(null);
  const busyRef = useRef(false);
  const isSelected = selectedPaymentType === "TILLGOOGLEPAY";

  const environment = TILL_ENVIRONMENT;

  const paymentRequest = useMemo(() => {
    const amountNum = Number(netAmount);
    if (!tokenizationSpecification || !amountNum || amountNum <= 0) return null;
    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [{ ...baseCardPaymentMethod, tokenizationSpecification }],
      merchantInfo: {
        merchantName: TILL_MERCHANT_NAME,
        ...(GOOGLE_MERCHANT_ID ? { merchantId: GOOGLE_MERCHANT_ID } : {}),
      },
      transactionInfo: {
        totalPriceStatus: "FINAL",
        totalPrice: amountNum.toFixed(2),
        currencyCode: TILL_CURRENCY,
        countryCode: TILL_COUNTRY,
      },
    };
  }, [netAmount]);

  const openSheetAndPay = async () => {
    const request = paymentRequest;
    const client = clientRef.current;
    if (!client || !request) {
      showFlashScreen(false);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    showFlashScreen(false);

    let paymentData;
    try {
      paymentData = await client.loadPaymentData(request);
    } catch (err) {
      busyRef.current = false;
      if (err?.statusCode === "CANCELED") return; // user dismissed the sheet — stay silent
      showNoteAlert("Google Pay could not be completed. Please try again or use a different payment method.");
      return;
    }

    const token = paymentData?.paymentMethodData?.tokenizationData?.token || null;
    if (!token) {
      busyRef.current = false;
      showNoteAlert("We couldn't read your Google Pay details. Please try again.");
      return;
    }

    try {
      // Create the WES order record + charge the Google Pay token through Till in one call.
      processOrder(token);
    } catch (err) {
      showFlashScreen(false);
      showNoteAlert("Google Pay could not be completed. Please try again or use a different payment method.");
    } finally {
      busyRef.current = false;
    }
  };

  const handleGateClick = () => {
    if (paybuttonDisabled || busyRef.current) return;
    if (typeof handleWalletPayClick !== "function") return;
    handleWalletPayClick(() => openSheetAndPay());
  };

  const onReadyToPayChange = (res) => {
    if (res?.isReadyToPay) setReady(true);
  };

  const onError = () => {};

  useEffect(() => {
    if (!paymentRequest) return;
    let cancelled = false;

    loadGooglePayScript()
      .then((google) => {
        if (cancelled) return null;
        if (!clientRef.current) {
          clientRef.current = new google.payments.api.PaymentsClient({ environment });
        }
        return clientRef.current.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [baseCardPaymentMethod],
        });
      })
      .then((res) => {
        if (cancelled || !res) return;
        if (res.result) setReady(true);
        else setAvailable(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailable(false);
      });

    return () => {
      cancelled = true;
    };
  }, [paymentRequest]);

  if (!available) return null;

  return (
    <div className={`choose-card payment-card-list shipPayment ${isSelected ? "selected" : ""}`}>
      <div className="shipping-method express-shipping " style={isSelected ? { borderBottom: "none" } : {}}>
        <div
          className="shipping-method-info pay-option"
          data-group={1}
          data-type="tillgooglepay"
          id="pay-tillgooglepay"
          style={{ gap: 0 }}
        >
          <div>
            <input
              className="form-check-input"
              type="radio"
              name="paymentMethod"
              id="tillGooglePayPayment"
              value="TILLGOOGLEPAY"
              onChange={() => handleOnCheck("TILLGOOGLEPAY")}
              onClick={() => handleOnCheck("TILLGOOGLEPAY")}
              checked={isSelected}
            />
          </div>
          <label className="form-check-label" htmlFor="tillGooglePayPayment" style={{ padding: 0 }}>
            <div className="full-width d-flex align-items-center" style={{ gap: 0, minWidth: "50px" }}>
              <div className="pay-icon">
                <CardIcon
                  cardType="google-pay"
                  alt="google pay"
                  style={{ width: "65px", height: "auto", objectFit: "contain" }}
                />
              </div>
              <div>Google Pay</div>
            </div>
          </label>
        </div>
      </div>

      {isSelected && (
        <div className="tillgpay-details-list" style={{ backgroundColor: "#ffffff" }}>
          <div
            className="add-new-card pt-5 pb-5"
            style={{
              minHeight: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 15,
              backgroundColor: "#ffffff",
            }}
          >
            <div className="total-amount-checkout">
              <h4>
                <strong>Total Amount $ {netAmount}</strong>
              </h4>
            </div>
            {!ready && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 80,
                  gap: 12,
                }}
              >
                <Spinner size={32} />
                <div style={{ fontSize: 13, color: "#666" }}>Loading Google Pay…</div>
              </div>
            )}

            <div
              style={{
                position: "relative",
                minWidth: 220,
                maxWidth: 320,
                minHeight: 40,
                opacity: paybuttonDisabled ? 0.5 : 1,
                pointerEvents: paybuttonDisabled ? "none" : "auto",
              }}
            >
              {paymentRequest && (
                <GooglePayButton
                  environment={environment}
                  paymentRequest={paymentRequest}
                  buttonColor="black"
                  buttonType="pay"
                  buttonSizeMode="fill"
                  buttonRadius="100"
                  buttonBorderType="default_border"
                  style={{ width: "100%", height: 40 }}
                  onReadyToPayChange={onReadyToPayChange}
                  onError={onError}
                />
              )}
              {/* Transparent overlay: captures the tap so Google's button never opens the sheet on its own.
                  We verify first, then open the sheet via our own loadPaymentData — all in one tap. */}
              <div onClick={handleGateClick} style={{ position: "absolute", inset: 0, zIndex: 2, cursor: "pointer" }} />
            </div>
            {ready && paybuttonDisabled && (
              <p style={{ fontSize: 13, color: "#666" }}>Please complete the required details before paying.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
